import React, { useState, useEffect, useRef, useCallback } from 'react';
import { storesService } from '../services/stores';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Eye } from 'lucide-react';

const MapView = () => {
  const [selectedStore, setSelectedStore] = useState(null);
  const [stores, setStores] = useState([]);
  const [featuredStores, setFeaturedStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Función para convertir porcentaje a píxeles basado en el tamaño real de la imagen
  const convertPercentageToPixels = useCallback((percentage, dimension) => {
    if (!percentage || typeof percentage !== 'string') return 0;
    const percentValue = parseFloat(percentage.replace('%', ''));
    return (percentValue / 100) * dimension;
  }, []);

  // Función para calcular las posiciones de los pins basadas en el tamaño real de la imagen
  const calculatePinPosition = useCallback((mapPosition) => {
    if (!mapPosition || !imageSize.width || !imageSize.height) {
      return { x: 0, y: 0 };
    }

    const x = convertPercentageToPixels(mapPosition.x, imageSize.width);
    const y = convertPercentageToPixels(mapPosition.y, imageSize.height);

    return { x, y };
  }, [imageSize, convertPercentageToPixels]);

  // Función para calcular el tamaño del pin basado en el tamaño de la imagen
  // Tamaño base: 48px para una imagen de 1920px de ancho
  const calculatePinSize = useCallback(() => {
    if (!imageSize.width) return { size: 48, fontSize: 12, borderWidth: 2 };
    
    // Tamaño base de referencia: 48px para 1920px de ancho
    const baseWidth = 1920;
    const baseSize = 48;
    const baseFontSize = 12;
    const baseBorderWidth = 2;
    
    // Calcular escala proporcional
    const scale = imageSize.width / baseWidth;
    
    return {
      size: Math.max(24, Math.min(64, baseSize * scale)), // Mínimo 24px, máximo 64px
      fontSize: Math.max(8, Math.min(16, baseFontSize * scale)), // Mínimo 8px, máximo 16px
      borderWidth: Math.max(1, Math.min(3, baseBorderWidth * scale)), // Mínimo 1px, máximo 3px
    };
  }, [imageSize.width]);

  // Función para actualizar el tamaño de la imagen y su offset
  const updateImageSize = useCallback(() => {
    if (imageRef.current && containerRef.current) {
      const img = imageRef.current;
      const container = containerRef.current;
      
      // Obtener el tamaño real de la imagen renderizada (no el contenedor)
      const imgRect = img.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      
      // Calcular el offset de la imagen dentro del contenedor
      // (para cuando object-contain deja espacio en los lados)
      const offsetX = imgRect.left - containerRect.left;
      const offsetY = imgRect.top - containerRect.top;
      
      setImageSize({
        width: imgRect.width,
        height: imgRect.height,
      });
      
      setImageOffset({
        x: offsetX,
        y: offsetY,
      });
    }
  }, []);

  // Efecto para manejar el resize de la ventana
  useEffect(() => {
    const handleResize = () => {
      // Pequeño delay para asegurar que el DOM se haya actualizado
      setTimeout(updateImageSize, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateImageSize]);

  // Efecto para cargar las tiendas
  useEffect(() => {
    const fetchStores = async () => {
      try {
        setLoading(true);
        const allStores = await storesService.getStores();
        setStores(allStores);
        
        // Cargar tiendas destacadas (usando is_featured o similar)
        const featured = await storesService.getFeaturedStores();
        setFeaturedStores(featured.slice(0, 5));
      } catch (error) {
        console.error('Error loading stores:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStores();
  }, []);

  // Handler para cuando la imagen se carga
  const handleImageLoad = useCallback(() => {
    updateImageSize();
  }, [updateImageSize]);

  return (
    <div className="px-6 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Mapa del outlet</h2>
          <p className="text-[#A2A2A2]">Toca una tienda para ver sus beneficios</p>
        </div>

        {/* Map Container */}
        <div className="relative bg-white/5 border border-white/10 rounded-3xl overflow-hidden">
          {/* Map Background - Imagen del mapa del outlet */}
          <div ref={containerRef} className="relative w-full">
            <img 
              ref={imageRef}
              src="/mapa_outlet.jpeg" 
              alt="Mapa del outlet"
              className="w-full h-auto object-contain block"
              onLoad={handleImageLoad}
            />
            {/* Overlay sutil para mejorar contraste de los pins */}
            <div className="absolute inset-0 bg-black/10 pointer-events-none"></div>

            {/* Store Pins */}
            {!loading && imageSize.width > 0 && imageSize.height > 0 && stores.map((store) => {
              if (!store.mapPosition) return null;
              
              // Calcular posición basada en el tamaño real de la imagen
              const pinPosition = calculatePinPosition(store.mapPosition);
              
              // Calcular tamaño del pin proporcional al mapa
              const pinSize = calculatePinSize();
              
              // Posición final del pin (offset de la imagen + posición relativa)
              const finalX = imageOffset.x + pinPosition.x;
              const finalY = imageOffset.y + pinPosition.y;
              
              // Tiendas participantes: "prendidas" (colores vivos, sin opacidad, resalte)
              // Tiendas NO participantes: "apagadas" (colores grises, con opacidad, desactivadas)
              return null
              return (
              <button
                key={store.id}
                onClick={() => store.isParticipating && setSelectedStore(store)}
                className={`absolute rounded-full flex items-center justify-center transition-all z-10 ${
                  store.isParticipating
                    ? 'bg-white border-white shadow-lg shadow-white/50 hover:scale-110 active:scale-95 cursor-pointer brightness-125'
                    : 'bg-white/10 border-white/20 cursor-not-allowed opacity-50 grayscale'
                }`}
                style={{
                  left: `${finalX}px`,
                  top: `${finalY}px`,
                  width: `${pinSize.size}px`,
                  height: `${pinSize.size}px`,
                  borderWidth: `${pinSize.borderWidth}px`,
                  transform: 'translate(-50%, -50%)',
                  fontSize: `${pinSize.fontSize}px`,
                }}
              >
                <span className={`font-bold ${
                  store.isParticipating ? 'text-black' : 'text-white/30'
                }`}>
                  {store.name.charAt(0)}
                </span>
              </button>
              );
            })}
          </div>
        </div>

        {/* Legend */}
        <div className="flex gap-6 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-white border border-white shadow-lg"></div>
            <span className="text-[#A2A2A2]">Tiendas participantes</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded-full bg-white/10 border border-white/20 opacity-50"></div>
            <span className="text-[#A2A2A2]">Próximamente</span>
          </div>
        </div>

        {/* Tiendas Destacadas - En la misma pantalla, debajo del mapa */}
        <div className="mt-12">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Tiendas destacadas</h2>
            <p className="text-[#A2A2A2]">Selección exclusiva para ti</p>
          </div>

          {/* Featured Stores Grid */}
          <div className="grid grid-cols-1 gap-4 mt-6">
            {loading && (
              <div className="text-center text-white py-8">
                Cargando tiendas destacadas...
              </div>
            )}
            {!loading && featuredStores.length === 0 && (
              <div className="text-center text-[#A2A2A2] py-8">
                No hay tiendas destacadas disponibles
              </div>
            )}
            {!loading && featuredStores.map((store) => (
              <div
                key={store.id}
                onClick={() => setSelectedStore(store)}
                className="bg-black border border-white/10 rounded-3xl p-6 hover:border-white/30 transition-all cursor-pointer hover:scale-[1.02] active:scale-[0.98]"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    {/* Logo/Imagen de la tienda destacada */}
                    {store.logo && (
                      <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
                        <img 
                          src={store.logo} 
                          alt={store.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-white mb-1 flex items-center gap-2">
                        {store.name} <Eye className="w-4 h-4 text-white" />
                      </h3>
                      <p className="text-[#A2A2A2] text-sm mb-3">{store.category}</p>
                      
                      <div className="bg-white/5 rounded-2xl p-4 border border-white/10 inline-block">
                        <p className="text-white font-medium">
                          {store.benefits && store.benefits.length > 0
                            ? `${store.benefits[0].title}${store.benefits.length > 1 ? ` +${store.benefits.length - 1}` : ''}`
                            : 'Sin beneficio activo'
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    className="border-white/10 text-white bg-transparent hover:bg-white/10 rounded-full px-4 py-2 text-sm"
                  >
                    Ver más
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Store Detail Dialog */}
      <Dialog open={!!selectedStore} onOpenChange={() => setSelectedStore(null)}>
        <DialogContent className="bg-black border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{selectedStore?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 pt-4">
            <div>
              <p className="text-[#A2A2A2] text-sm mb-1">Categoría</p>
              <p className="text-white">{selectedStore?.category}</p>
            </div>
            
            <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                <p className="text-[#A2A2A2] text-sm mb-2">Beneficios</p>
                {selectedStore?.benefits && selectedStore.benefits.length > 0 ? (
                  <ul className="space-y-2">
                    {selectedStore.benefits.map((b) => (
                      <li key={b.id} className="text-white">
                        <div className="font-medium">{b.title}</div>
                        {b.description && <div className="text-[#A2A2A2] text-sm">{b.description}</div>}
                        <div className="text-[#A2A2A2] text-xs">Vigencia: {b.validUntil ?? '—'}</div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-white font-medium text-lg">Sin beneficio activo</p>
                )}
            </div>

            {/* <div>
              <p className="text-[#A2A2A2] text-sm mb-2">Descripción</p>
              <p className="text-white">{selectedStore?.description}</p>
            </div> */}

            {/* <div>
              <p className="text-[#A2A2A2] text-sm mb-2">Vigencia</p>
              <p className="text-white">Hasta {selectedStore?.validUntil}</p>
            </div> */}

            {selectedStore?.conditions && selectedStore.conditions.length > 0 && (
              <div>
                <p className="text-[#A2A2A2] text-sm mb-2">Condiciones</p>
                <ul className="space-y-1">
                  {selectedStore.conditions.map((condition, index) => (
                    <li key={index} className="text-white text-sm flex items-start gap-2">
                      <span className="text-[#A2A2A2]">•</span>
                      <span>{condition}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Leyenda de uso del beneficio */}
            {selectedStore?.benefits && selectedStore.benefits.length > 0 && (
              <div className="bg-white/5 rounded-2xl p-4 border border-white/10">
                <p className="text-[#A2A2A2] text-xs leading-relaxed text-center">
                  Muestra tu QR en la tienda para aplicar el beneficio.<br />
                  Consulta términos y condiciones en tienda. Exclusivo para miembros CCE Puebla
                </p>
              </div>
            )}

            <Button
              onClick={() => setSelectedStore(null)}
              className="w-full bg-white text-black hover:bg-white/90 rounded-full py-6 transition-all hover:scale-105 active:scale-95"
            >
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MapView;
